import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "../lib/auth-context";
import { BrandMark } from "../components/Brand";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSigninUrl() {
  return `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start?next=${encodeURIComponent("/dashboard")}`;
}

export default function SignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [collisionEmail, setCollisionEmail] = useState(null);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCollisionEmail(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/signup", {
        email,
        password,
        name,
        workspace_name: workspaceName,
      });
      await loginWithToken(data.access_token);
      track("signup_completed", { plan: "free", role: "admin" });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "email_registered_with_google") {
        setCollisionEmail(email);
        setError("");
      } else {
        setError(typeof detail === "string" ? detail : "Sign up failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50" data-testid="signup-page">
      <header className="border-b border-ink-200 bg-white">
        <div className="hp-container flex h-16 items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/signin" className="hp-btn-ghost" data-testid="signup-to-signin">
            Already have an account? Sign in
          </Link>
        </div>
      </header>
      <main className="flex items-start justify-center px-6 py-16">
        <div className="w-full max-w-[460px]">
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink-900">
            Create your workspace
          </h1>
          <p className="mt-2 text-[14px] text-ink-500">
            Two free interviews. No credit card. Cancel any time.
          </p>

          <button
            type="button"
            onClick={() => (window.location.href = googleSigninUrl())}
            className="hp-btn-outline mt-8 w-full"
            data-testid="signup-google-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.1A6.999 6.999 0 015.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A10.997 10.997 0 001 12c0 1.78.43 3.45 1.18 4.93l3.66-2.83z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            Sign up with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-[12px] text-ink-400">
            <div className="hp-divider flex-1" />
            <span className="uppercase tracking-wider">or with email</span>
            <div className="hp-divider flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Full name
              </label>
              <input
                type="text"
                required
                autoComplete="name"
                className="hp-input mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="signup-name"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Work email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                className="hp-input mt-1.5"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Password (min 8 chars)
              </label>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="hp-input mt-1.5"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Workspace name
              </label>
              <input
                type="text"
                required
                placeholder="Acme HR"
                className="hp-input mt-1.5"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                data-testid="signup-workspace"
              />
              <p className="mt-1.5 text-[12px] text-ink-400">
                Usually your company name. Shown to invited teammates.
              </p>
            </div>

            {collisionEmail && (
              <div
                className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900 space-y-2"
                data-testid="signup-collision-banner"
              >
                <div>
                  <strong>{collisionEmail}</strong> is already registered with Google sign-in.
                </div>
                <button
                  type="button"
                  onClick={() => (window.location.href = googleSigninUrl())}
                  className="hp-btn-primary w-full"
                  data-testid="signup-collision-google-cta"
                >
                  Continue with Google
                </button>
              </div>
            )}
            {error && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700"
                data-testid="signup-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="hp-btn-primary w-full"
              data-testid="signup-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              Create workspace
            </button>
            <p className="text-[12px] text-ink-400">
              By creating a workspace, you agree to Hiretape&rsquo;s Terms and Privacy Policy.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
